'use client';

import React, {ReactNode, createContext, useContext, useState, useEffect} from 'react';

import {TrackableDiscoveryItem} from '@udemy-v2/react-browse-event-tracking';
import {BrowseCourse} from '@udemy-v2/browse-course';
import * as Sentry from '@sentry/nextjs';
import {
    ProductCardCertificationVoucher,
    getBundleFromCourse,
} from '@udemy-v2/react-certification-preparation';
import {generateTrackingId} from '@udemy-v2/event-tracking';
import {useAvailableExamAndVoucherForTopics} from '@udemy-v2/react-certification-vouchers';

import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';
import {fetchCourseDataByIds} from '@/app/lib/course/course-fetcher';

export interface VoucherContext {
    // Whether or not we are still loading voucher-related data.
    isLoading: boolean;
    // The certification voucher we want to display for the course.
    certificationVoucher: TrackableDiscoveryItem<ProductCardCertificationVoucher> | null;
    certificationTopicId: string | null;
    // A voucher-related bundle of courses, if any.
    bundledCertificationCourses: TrackableDiscoveryItem<BrowseCourse>[];
}

export const ReactCLPVoucherContext = createContext<VoucherContext | undefined>(undefined);

export interface CLPVoucherContextProviderProps {
    children: ReactNode;
}

export const CLPVoucherContextProvider: React.FC<CLPVoucherContextProviderProps> = (props) => {
    const course = useClpCourse();

    const [bundledCourses, setBundledCourses] = useState<TrackableDiscoveryItem<BrowseCourse>[]>(
        [],
    );
    const [loadingCourses, setLoadingCourses] = useState<boolean>(true);

    // Build ordered list of topic IDs: primary topic first, then related topics as fallbacks.
    // This handles courses where the primary topic is an aggregate (e.g. "Amazon AWS") with no
    // exam, while the specific cert topic (e.g. "AWS Certified Solutions Architect") lives in
    // the related topics array.
    const voucherTopicIds: string[] = [];
    if (course?.primaryTopic?.id != null) voucherTopicIds.push(String(course.primaryTopic.id));
    course?.topics?.forEach((topic) => {
        const id = String(topic.id);
        if (!voucherTopicIds.includes(id)) voucherTopicIds.push(id);
    });
    const {
        isLoading: isLoadingVouchers,
        exam,
        selectedVoucherPool,
    } = useAvailableExamAndVoucherForTopics(voucherTopicIds);

    useEffect(() => {
        (async () => {
            try {
                const bundledCourseIds = (
                    getBundleFromCourse(parseInt(course.id, 10))?.contents ?? []
                )
                    .map((buyable) => buyable.id)
                    .map((courseId) => courseId.toString());
                if (bundledCourseIds.length > 0) {
                    const bundledCourses = (await fetchCourseDataByIds(bundledCourseIds)).filter(
                        (bundleCourse) =>
                            bundleCourse.isPracticeTestCourse !== course.isPracticeTestCourse,
                    );
                    setBundledCourses(bundledCourses);
                }
            } catch (error) {
                // Don't care, we can proceed even with no bundled courses.
                Sentry.captureException(error);
            } finally {
                setLoadingCourses(false);
            }
        })();
    }, [course.id, course.isPracticeTestCourse]);

    return (
        <ReactCLPVoucherContext.Provider
            value={{
                isLoading: isLoadingVouchers || loadingCourses,
                certificationTopicId: exam?.topicId ?? null,
                certificationVoucher: selectedVoucherPool,
                bundledCertificationCourses: bundledCourses.map((course) => ({
                    ...course,
                    frontendTrackingId: generateTrackingId(),
                })),
            }}
        >
            {props.children}
        </ReactCLPVoucherContext.Provider>
    );
};

/**
 * Custom hook to access the CLP purchase context
 * @returns CLPVoucherContext
 * @throws Error if used outside of CLPVoucherContextProvider
 */
export function useClpVoucherContext(): VoucherContext {
    const context = useContext(ReactCLPVoucherContext);

    if (context === undefined) {
        throw new Error('useClpVoucherContext must be used within a CLPVoucherContextProvider');
    }

    return context;
}
